import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import type { TrackingFilters, StatusFilter, LocationStatusFilter } from '../../types/tracking';
import { PROGRAM_OPTIONS } from '../../utils/constants';

interface FilterBarProps {
  filters: TrackingFilters;
  onDateChange: (date: string) => void;
  onToggleProgram: (program: string) => void;
  onStatusChange: (status: StatusFilter) => void;
  onLocationStatusChange: (status: LocationStatusFilter) => void;
  onSearchChange: (query: string) => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onDateChange,
  onToggleProgram,
  onStatusChange,
  onLocationStatusChange,
  onSearchChange,
}) => {
  const [searchInput, setSearchInput] = useState(filters.searchQuery);
  const [programOpen, setProgramOpen] = useState(false);
  const programRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounced search
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      onSearchChange(searchInput);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput, onSearchChange]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (programRef.current && !programRef.current.contains(e.target as Node)) {
        setProgramOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'live', label: 'Live' },
    { value: 'offline', label: 'Offline' },
  ];

  return (
    <div className="space-y-3 p-4 border-b border-rgf-gray-200">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rgf-gray-400" />
        <input
          type="text"
          placeholder="Search by name, role, or RGF ID..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 outline-none"
        />
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        {/* Date picker */}
        <input
          type="date"
          value={filters.date}
          onChange={e => onDateChange(e.target.value)}
          className="px-2 py-1.5 text-sm border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 outline-none"
        />

        {/* Program multi-select dropdown */}
        <div className="relative" ref={programRef}>
          <button
            onClick={() => setProgramOpen(!programOpen)}
            className="flex items-center gap-1 px-2 py-1.5 text-sm border border-rgf-gray-300 rounded-lg hover:bg-rgf-gray-50"
          >
            {filters.programs.length === 0
              ? 'All Programs'
              : `${filters.programs.length} program${filters.programs.length > 1 ? 's' : ''}`}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>

          {programOpen && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-rgf-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
              {PROGRAM_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-rgf-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filters.programs.includes(opt.value)}
                    onChange={() => onToggleProgram(opt.value)}
                    className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Location status dropdown */}
        <select
          value={filters.locationStatus}
          onChange={e => onLocationStatusChange(e.target.value as LocationStatusFilter)}
          className="px-2 py-1.5 text-sm border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 outline-none"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {/* Status toggle buttons */}
      <div className="flex gap-1 bg-rgf-gray-100 rounded-lg p-0.5">
        {statusOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => onStatusChange(opt.value)}
            className={clsx(
              'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
              filters.status === opt.value
                ? 'bg-white text-rgf-primary-700 shadow-sm'
                : 'text-rgf-gray-600 hover:text-rgf-gray-900'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};
